import { AfterViewInit, Directive, ElementRef, EventEmitter, NgZone, OnDestroy, Output } from '@angular/core';

/**
 * Keeps a scroll container pinned to the bottom as content grows — but only
 * while the user is already at (or very near) the bottom. If they scroll up to
 * read history, new content no longer yanks them back down.
 *
 * Pinning rule: scroll position alone NEVER unpins. Programmatic sticks, lazy
 * `content-visibility` layout and browser scroll-anchoring all move/resize the
 * scroller and fire scroll events that look identical to a user scrolling away —
 * inferring intent from them unpins randomly mid-stream. Instead, only explicit
 * user gestures (wheel, touch drag, scrollbar drag, navigation keys) can unpin;
 * reaching the bottom again (by any means) re-pins.
 *
 * All of it runs outside the Angular zone: following a streaming response to the
 * bottom is pure DOM work and must not cost a change-detection pass per delta.
 * This is the Angular counterpart of T3 Code's `maintainScrollAtEnd` behaviour.
 */
@Directive({
	standalone: false,
	selector:   '[csStickToBottom]'
})
export class CsStickToBottomDirective implements AfterViewInit, OnDestroy {
	/**
	 * Pin-state changes, emitted INSIDE the zone but only on actual transitions —
	 * hosts use it to show a "scroll to bottom" affordance while unpinned, without
	 * paying a change-detection pass per scroll event.
	 */
	@Output() readonly csStickPinnedChange = new EventEmitter<boolean>();

	private _pinned = true;
	private rafScheduled = false;
	private observer: MutationObserver | null = null;
	private readonly thresholdPx = 48;
	/** True while a user gesture (wheel/touch/scrollbar-drag/keys) is in flight. */
	private userGestureUntil = 0;
	/** While > now, force the bottom and ignore everything else (explicit pin()). */
	private forceUntil = 0;

	constructor(
		private readonly el: ElementRef<HTMLElement>,
		private readonly zone: NgZone
	) {}

	private get pinned(): boolean { return this._pinned; }

	private set pinned(value: boolean) {
		if (value === this._pinned) { return; }
		this._pinned = value;
		this.zone.run(() => this.csStickPinnedChange.emit(value));
	}

	ngAfterViewInit(): void {
		const node = this.el.nativeElement;
		this.zone.runOutsideAngular(() => {
			node.addEventListener('scroll', this.onScroll, { passive: true });
			node.addEventListener('wheel', this.onUserGesture, { passive: true });
			node.addEventListener('touchmove', this.onUserGesture, { passive: true });
			node.addEventListener('pointerdown', this.onUserGesture, { passive: true });
			node.addEventListener('keydown', this.onUserGesture, { passive: true });
			this.observer = new MutationObserver(() => this.scheduleStick());
			this.observer.observe(node, { childList: true, subtree: true, characterData: true });
			this.stick();
		});
	}

	ngOnDestroy(): void {
		const node = this.el.nativeElement;
		node.removeEventListener('scroll', this.onScroll);
		node.removeEventListener('wheel', this.onUserGesture);
		node.removeEventListener('touchmove', this.onUserGesture);
		node.removeEventListener('pointerdown', this.onUserGesture);
		node.removeEventListener('keydown', this.onUserGesture);
		this.observer?.disconnect();
		this.observer = null;
	}

	/**
	 * Force the container to the bottom and re-pin. Call this on an explicit user
	 * action (e.g. sending a prompt): unlike passive streaming growth, sending
	 * should always jump to the latest message regardless of scroll position.
	 * The frame loop rides out the lazy layout shifts that follow the jump.
	 */
	pin(durationMs = 400): void {
		this.pinned = true;
		this.userGestureUntil = 0;
		this.forceUntil = performance.now() + durationMs;
		this.zone.runOutsideAngular(() => {
			const tick = (): void => {
				this.stick();
				if (performance.now() < this.forceUntil) {
					requestAnimationFrame(tick);
				}
			};
			tick();
		});
	}

	/** A real input gesture: scrolls in the next ~250ms reflect user intent. */
	private readonly onUserGesture = (): void => {
		if (performance.now() < this.forceUntil) {
			return;
		}
		this.userGestureUntil = performance.now() + 250;
	};

	private readonly onScroll = (): void => {
		if (performance.now() < this.forceUntil) {
			this.pinned = true;
			return;
		}
		const node     = this.el.nativeElement;
		const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight <= this.thresholdPx;

		if (atBottom) {
			this.pinned = true;            // reaching the bottom always re-pins
		} else if (performance.now() < this.userGestureUntil) {
			this.pinned = false;           // only a user gesture can unpin
		}
		// Otherwise: a programmatic/layout scroll left us off-bottom — keep the
		// current pin and let the next stick catch up.
	};

	private scheduleStick(): void {
		if (this.rafScheduled) {
			return;
		}
		this.rafScheduled = true;
		requestAnimationFrame(() => {
			this.rafScheduled = false;
			if (this.pinned) {
				this.stick();
			}
		});
	}

	private stick(): void {
		const node = this.el.nativeElement;
		node.scrollTop = node.scrollHeight;
	}
}
